import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemoizedFn, useTheme, useUnmount, useUpdateEffect } from 'ahooks';
import { Typography } from '@mui/material';
import { useBizState } from '@/hooks/use-biz-state';
import { useConfigStore } from '@/store/use-config-store';
import {
  cleanScript,
  executeV3,
  getRecaptchaLang,
  getScriptUrl,
  loadScript,
  renderV2,
  resetV2,
} from '@/utils/recaptcha';
import './index.css';

export type GoogleCaptchaProps = {
  action?: string;
  onTokenChange: (token: string) => void;
  onLoadingChange: (loading: boolean) => void;
};

export type GoogleCaptchaRef = {
  validation: () => boolean;
  reload: () => void;
};

export const GoogleCaptcha = forwardRef<GoogleCaptchaRef, GoogleCaptchaProps>((props, ref) => {
  const { theme } = useTheme();
  const { i18n, t } = useTranslation();
  const { location, gsk2, gsk3 } = useConfigStore();
  const { googleVersion } = useBizState();
  const [, setLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [v2Ready, setV2Ready] = useState<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const isV2 = googleVersion === '2';
  const isV3 = googleVersion === '3';

  const clearTimer = useMemoizedFn(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  });

  useUnmount(() => {
    clearTimer();
  });

  const getWidgetId = useMemoizedFn(() => {
    return document.getElementById(`recaptcha-${i18n.language}-${theme}`) as HTMLDivElement;
  });

  const handleTokenChange = useMemoizedFn((token: string) => {
    setToken(token);
    props.onTokenChange(token);
    if (token) setShowError(false);
  });

  const handleLoadingChange = useMemoizedFn((loading: boolean) => {
    setLoading(loading);
    props.onLoadingChange(loading);
  });

  const doneLoading = useMemoizedFn(() => {
    clearTimer();
    handleLoadingChange(false);
  });

  const startV2 = useMemoizedFn(() => {
    const widgetId = getWidgetId();

    timerRef.current = setTimeout(() => {
      setV2Ready(false);
      handleLoadingChange(false);
      setToken('');
    }, 10000);

    renderV2(widgetId, {
      sitekey: gsk2,
      action: props.action || 'LOGIN',
      theme,
      hl: getRecaptchaLang(i18n.language),
      callback: handleTokenChange,
      'expired-callback': resetV2,
      'error-callback': resetV2,
    })
      .then(() => {
        setV2Ready(true);
      })
      .catch(() => {
        setV2Ready(false);
        resetV2();
      })
      .finally(doneLoading);
  });

  const startV3 = useMemoizedFn(() => {
    if (!gsk3) {
      doneLoading();
      return;
    }

    timerRef.current = setTimeout(() => {
      handleLoadingChange(false);
      setToken('');
    }, 10000);

    executeV3(gsk3, props.action || 'LOGIN')
      .then(handleTokenChange)
      .catch(() => {
        setToken('');
      })
      .finally(doneLoading);
  });

  const init = useMemoizedFn(() => {
    if ((isV3 && !gsk3) || (isV2 && !gsk2)) return;
    const hl = getRecaptchaLang(i18n.language);
    const url = getScriptUrl(googleVersion, gsk3, location, hl);

    clearTimer();

    if (isV2) setV2Ready(false);
    handleTokenChange('');
    handleLoadingChange(true);

    loadScript(url)
      .then(() => {
        if (isV2) startV2();
        else startV3();
      })
      .catch(() => {
        clearTimer();
        if (isV2) setV2Ready(false);
        setToken('');
        handleLoadingChange(false);
      });
  });

  const reload = useMemoizedFn(() => {
    cleanScript();
    getWidgetId()?.replaceChildren();
    init();
  });

  const validate = () => {
    const isValid = !isV2 || !v2Ready || !!token;
    setShowError(isV2 && v2Ready && !token);
    return isValid;
  };

  useUpdateEffect(() => {
    reload();
  }, [googleVersion]);

  useUpdateEffect(() => {
    if (isV3) return;
    reload();
  }, [i18n.language, theme]);

  useUpdateEffect(() => {
    init();
  }, [gsk2, gsk3, location]);

  useEffect(() => {
    if (import.meta.env.DEV) {
      const timer = window.setTimeout(init, 0);
      return () => window.clearTimeout(timer);
    }

    init();
  }, []);

  useImperativeHandle(ref, () => ({
    validation: validate,
    reload,
  }));

  return (
    <div>
      <div id={`recaptcha-${i18n.language}-${theme}`} />
      {showError && (
        <Typography variant="caption" color="error" sx={{ mx: 1 }} role="alert">
          {t('recaptcha_required_message')}
        </Typography>
      )}
    </div>
  );
});
